// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Fuzzy Matcher
// Production-grade string similarity for matching messy ingredient names
// to canonical catalog entries. Arabic-aware + multi-algorithm scoring.
// ══════════════════════════════════════════════════════════════════════════════

// ─── Normalization ────────────────────────────────────────────────────────
// Strips Arabic diacritics, normalizes alef forms, removes punctuation, lowercases

const ARABIC_DIACRITICS = /[\u064B-\u065F\u0670\u0640]/g;
const ALEF_FORMS = /[آأإٱ]/g; // normalize to ا
const YEH_FORMS = /[ىی]/g;    // normalize to ي
const TEH_MARBUTA = /[ة]/g;    // normalize to ه
const NON_WORD = /[^\p{L}\p{N}\s]/gu;
const MULTI_SPACE = /\s+/g;

export function normalizeText(text) {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .replace(ARABIC_DIACRITICS, '')
    .replace(ALEF_FORMS, 'ا')
    .replace(YEH_FORMS, 'ي')
    .replace(TEH_MARBUTA, 'ه')
    .replace(NON_WORD, ' ')
    .replace(MULTI_SPACE, ' ')
    .trim();
}

// ─── Levenshtein distance ─────────────────────────────────────────────────
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a?.length) return b?.length || 0;
  if (!b?.length) return a.length;

  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);

  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

export function levenshteinSimilarity(a, b) {
  const maxLen = Math.max(a?.length || 0, b?.length || 0);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
}

// ─── Jaro-Winkler similarity ─────────────────────────────────────────────
export function jaroWinkler(a, b) {
  if (a === b) return 1;
  if (!a?.length || !b?.length) return 0;

  const matchWindow = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const aMatches = new Array(a.length).fill(false);
  const bMatches = new Array(b.length).fill(false);

  let matches = 0;
  for (let i = 0; i < a.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(b.length, i + matchWindow + 1);
    for (let j = start; j < end; j++) {
      if (bMatches[j] || a[i] !== b[j]) continue;
      aMatches[i] = true;
      bMatches[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aMatches[i]) continue;
    while (!bMatches[k]) k++;
    if (a[i] !== b[k]) transpositions++;
    k++;
  }

  const jaro = (
    matches / a.length +
    matches / b.length +
    (matches - transpositions / 2) / matches
  ) / 3;

  let prefixLen = 0;
  for (let i = 0; i < Math.min(4, a.length, b.length); i++) {
    if (a[i] === b[i]) prefixLen++;
    else break;
  }
  return jaro + prefixLen * 0.1 * (1 - jaro);
}

// ─── Token set similarity ────────────────────────────────────────────────
export function tokenSetRatio(a, b) {
  const tokensA = new Set((a || '').split(/\s+/).filter(Boolean));
  const tokensB = new Set((b || '').split(/\s+/).filter(Boolean));
  if (tokensA.size === 0 && tokensB.size === 0) return 1;
  if (tokensA.size === 0 || tokensB.size === 0) return 0;

  const intersection = new Set([...tokensA].filter(x => tokensB.has(x)));
  const union = new Set([...tokensA, ...tokensB]);
  return intersection.size / union.size;
}

// ─── Containment score ────────────────────────────────────────────────────
export function containmentScore(a, b) {
  if (!a || !b) return 0;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (longer.includes(shorter)) {
    return shorter.length / longer.length;
  }
  return 0;
}

// ─── Combined ensemble score ─────────────────────────────────────────────
export function similarityScore(a, b) {
  const normA = normalizeText(a);
  const normB = normalizeText(b);

  if (normA === normB) return 1;
  if (!normA || !normB) return 0;

  const lev = levenshteinSimilarity(normA, normB);
  const jw = jaroWinkler(normA, normB);
  const tok = tokenSetRatio(normA, normB);
  const cont = containmentScore(normA, normB);

  return (lev * 0.25 + jw * 0.25 + tok * 0.30 + cont * 0.20);
}

// ─── Find best matches ────────────────────────────────────────────────────
export function findBestMatches(input, candidates, options = {}) {
  const {
    threshold = 0.65,
    topN = 5,
    getCandidateText = (c) => c
  } = options;

  if (!input || !candidates?.length) return [];

  return candidates
    .map(candidate => ({
      candidate,
      score: similarityScore(input, getCandidateText(candidate))
    }))
    .filter(x => x.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
}

// ─── Confidence classifier ────────────────────────────────────────────────
export function classifyConfidence(score) {
  if (score >= 0.92) return 'high';
  if (score >= 0.75) return 'medium';
  if (score >= 0.60) return 'low';
  return 'none';
}
